import React, { useEffect, useState } from 'react';
import { useLive } from '../../live.jsx';
import PriceTicker from '../../components/PriceTicker.jsx';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../../api.js';
import { useI18n, usePageTitle } from '../../i18n/index.jsx';
import { useAuth } from '../../auth.jsx';
import { useCategoryTree, nodeName } from '../../categoryTree.js';
import CategoryTile from '../../components/CategoryTile.jsx';
import PageHero from '../../components/PageHero.jsx';
import { useContent } from '../../content.jsx';
import ListingCard from '../../components/ListingCard.jsx';
import { Alert } from '../../components/ui.jsx';
import TutorialSlot from '../../tutorials/TutorialSlot.jsx';
import RequestsBrowse from '../requests/RequestsBrowse.jsx';

// Public browse page. Filters live in the URL (?q=&category=&spec=&page=),
// so results can be shared and the home-page sector buttons can link here
// with a category (or several, comma-separated) already selected. A
// category includes everything below it (any level); with one category
// chosen, the page shows its next level as photo tiles and its spec
// fields as filters.
// Tabs (?tab=): products for sale (default) | buying requests (migration
// 042) | all (latest matching requests above the products). Search and
// category apply to both.
const TILE_LIMIT = 12;
const TABS = ['products', 'requests', 'all'];
const REQUEST_KEYS = ['country', 'qmin', 'qmax', 'incoterm', 'posted', 'state'];
export default function Marketplace() {
  const { t, lang, errorText } = useI18n();
  const { user } = useAuth();
  const { text, value } = useContent('marketplace');
  usePageTitle('marketplace.title');
  const tree = useCategoryTree();
  const [params, setParams] = useSearchParams();
  const q = params.get('q') || '';
  const category = params.get('category') || '';
  const spec = params.get('spec') || '';
  const page = Number(params.get('page')) || 1;
  const tab = TABS.includes(params.get('tab')) ? params.get('tab') : 'products';
  const [query, setQuery] = useState(q);
  const [data, setData] = useState(null);
  const [facets, setFacets] = useState([]);
  const [allTiles, setAllTiles] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setQuery(q);
  }, [q]);
  const [tick, setTick] = useState(0);
  useLive(['listings', 'catalogue'], () => setTick((x) => x + 1)); // products approved / hidden: read again
  useEffect(() => {
    if (tab === 'requests') return undefined;
    let alive = true;
    setError('');
    const qs = new URLSearchParams();
    if (q) qs.set('q', q);
    if (category) qs.set('category', category);
    if (spec) qs.set('spec', spec);
    if (page > 1) qs.set('page', page);
    api(`/marketplace/listings?${qs}`)
      .then((d) => alive && setData(d))
      .catch((err) => alive && setError(errorText(err)));
    qs.delete('page');
    if (category && !category.includes(',')) api(`/marketplace/facets?${qs}`).then((d) => alive && setFacets(d.fields)).catch(() => alive && setFacets([]));
    else setFacets([]);
    return () => { alive = false; };
  }, [q, category, spec, page, errorText, tick, tab]);
  useEffect(() => { setAllTiles(false); }, [category]);

  const update = (changes) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(changes)) v ? next.set(k, v) : next.delete(k);
    if (!('page' in changes)) next.delete('page');
    if ('category' in changes) next.delete('spec');
    setParams(next);
  };
  const setTab = (k) => {
    const next = new URLSearchParams(params);
    if (k === 'products') next.delete('tab'); else next.set('tab', k);
    next.delete('page');
    if (k === 'products') REQUEST_KEYS.forEach((x) => next.delete(x)); else next.delete('spec');
    setParams(next);
  };

  const selected = category.split(',').filter(Boolean);
  const isMulti = selected.length > 1;
  const pages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;
  const filtered = q || category || spec;
  const current = !isMulti && category ? tree.bySlug(category) : null;
  const crumbs = current ? tree.path(current.id) : [];
  // Next level: categories with products first.
  const level = [...tree.children(current ? current.id : null)].sort((a, b) => (b.liveCount > 0) - (a.liveCount > 0));
  const tiles = allTiles ? level : level.slice(0, TILE_LIMIT);
  // Spec filters: field:value pairs; several values of one field = any of them.
  const chosen = spec.split('|').filter(Boolean);
  const toggleSpec = (key, value) => {
    const pair = `${key}:${value}`;
    update({ spec: (chosen.includes(pair) ? chosen.filter((x) => x !== pair) : [...chosen, pair]).join('|'), page: '' });
  };
  const label = (f) => (lang === 'en' ? f.labelEn || f.labelFr : f.labelFr);
  const optionLabel = (n) => `${'\u00a0\u00a0'.repeat(tree.path(n.id).length - 1)}${nodeName(n, lang)}`;
  const options = [];
  const walk = (id) => tree.children(id).forEach((n) => { options.push(n); walk(n.id); });
  walk(null);

  return (
    <>
      <PriceTicker />
      <PageHero title={text('title', 'marketplace.title')} lead={text('lead', 'marketplace.lead')} image={value('banner') || undefined}>
        {!user && <div className="hero-actions"><Link className="btn btn-primary" to="/signup">{t('marketplace.sell')}</Link></div>}
        <TutorialSlot place="site.marketplace" />
      </PageHero>
      <section className="section section-tight">
        <div className="container">
          <div className="market-tabs" role="tablist" aria-label={t('marketplace.title')}>
            {TABS.map((k) => (
              <button key={k} type="button" role="tab" aria-selected={tab === k} className={`${tab === k ? 'on' : ''}${k === 'requests' ? ' tab-wanted' : ''}`} onClick={() => setTab(k)}>
                {k === 'requests' && <span className="wanted-badge" aria-hidden="true">{t('buying.wanted')}</span>} {t(`buying.tabs.${k}`)}
              </button>
            ))}
          </div>
          <form className="market-filters" role="search" onSubmit={(e) => { e.preventDefault(); update({ q: query.trim() }); }}>
            <label className="sr-only" htmlFor="m-q">{t('marketplace.searchLabel')}</label>
            <input id="m-q" type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('marketplace.searchPlaceholder')} />
            <label className="sr-only" htmlFor="m-cat">{t('marketplace.category')}</label>
            <select id="m-cat" value={isMulti ? '__multi' : category} onChange={(e) => update({ category: e.target.value })}>
              <option value="">{t('marketplace.allCategories')}</option>
              {isMulti && (
                <option value="__multi" disabled>
                  {selected.map((x) => nodeName(tree.bySlug(x), lang) || x).join(' + ')}
                </option>
              )}
              {options.map((n) => <option key={n.slug} value={n.slug}>{optionLabel(n)}</option>)}
            </select>
            <button type="submit" className="btn btn-primary">{t('marketplace.search')}</button>
          </form>

          {tab === 'requests' && <RequestsBrowse params={params} update={update} />}
          {tab === 'all' && <RequestsBrowse params={params} update={update} compact onSeeAll={() => setTab('requests')} />}
          {tab !== 'requests' && (
          <>
          {current && (
            <div className="market-cat-head">
              <nav className="wz-crumbs" aria-label={t('wizard.breadcrumb')}>
                <button type="button" onClick={() => update({ category: '' })}>{t('marketplace.allCategories')}</button>
                {crumbs.map((n) => (
                  <React.Fragment key={n.id}>
                    <span aria-hidden="true">›</span>
                    <button type="button" onClick={() => update({ category: n.slug })} aria-current={n.id === current.id ? 'page' : undefined}>{nodeName(n, lang)}</button>
                  </React.Fragment>
                ))}
              </nav>
              <h2>{nodeName(current, lang)}</h2>
              {(lang === 'en' ? current.descriptionEn : current.descriptionFr) && <p className="muted">{lang === 'en' ? current.descriptionEn : current.descriptionFr}</p>}
            </div>
          )}
          {!isMulti && !q && level.length > 0 && (
            <div className="market-tiles">
              <div className="cat-grid">
                {tiles.map((n) => (
                  <CategoryTile key={n.id} tree={tree} node={n} lang={lang} LinkAs={Link} href={`/marketplace?category=${encodeURIComponent(n.slug)}`}
                    sub={n.liveCount ? t('marketplace.productsN', { n: n.liveCount }) : t('marketplace.noProductsYet')} />
                ))}
              </div>
              {level.length > TILE_LIMIT && <button type="button" className="btn btn-link btn-sm" onClick={() => setAllTiles(!allTiles)}>{allTiles ? t('marketplace.fewerCategories') : t('marketplace.allSubcategories', { n: level.length })}</button>}
            </div>
          )}
          {facets.length > 0 && (
            <div className="market-facets" role="group" aria-label={t('marketplace.filters')}>
              {facets.map((f) => (
                <div key={f.key} className="market-facet">
                  <span className="field-label">{label(f)}</span>
                  <div className="chip-choice">
                    {f.values.map((v) => {
                      const on = chosen.includes(`${f.key}:${v.value}`);
                      return <button key={v.value} type="button" className={`chip-toggle${on ? ' on' : ''}`} aria-pressed={on} onClick={() => toggleSpec(f.key, v.value)}>{lang === 'en' ? v.valueEn : v.value} <span className="facet-n">{v.count}</span></button>;
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="market-meta">
            {data && <p className="muted">{t('marketplace.results', { n: data.total })}</p>}
            {filtered && <button type="button" className="btn btn-link btn-sm" onClick={() => setParams({})}>{t('marketplace.clear')}</button>}
          </div>

          <Alert>{error}</Alert>
          {!data && !error && <p className="muted">{t('common.loading')}</p>}
          {data && data.items.length === 0 && (
            <div className="empty-state">
              <p>{filtered ? t('marketplace.noResults') : t('marketplace.noResultsEmpty')}</p>
            </div>
          )}
          {data && data.items.length > 0 && (
            <div className="listing-grid">
              {data.items.map((l) => <ListingCard key={l.id} listing={l} />)}
            </div>
          )}
          {data && pages > 1 && (
            <nav className="pager" aria-label={t('marketplace.page', { n: page, total: pages })}>
              <button type="button" className="btn btn-ghost btn-sm" disabled={page <= 1} onClick={() => update({ page: page - 1 })}>{t('marketplace.prev')}</button>
              <span className="muted">{t('marketplace.page', { n: page, total: pages })}</span>
              <button type="button" className="btn btn-ghost btn-sm" disabled={page >= pages} onClick={() => update({ page: page + 1 })}>{t('marketplace.next')}</button>
            </nav>
          )}
          </>
          )}
        </div>
      </section>

      {/* "Become a seller" band — for visitors and members who don't sell yet. */}
      {!user?.isSeller && (
        <section className="sell-band">
          <div className="container sell-band-inner">
            <div>
              <h2>{text('sellTitle', 'marketplace.sellBandTitle')}</h2>
              <p>{text('sellText', 'marketplace.sellBandText')}</p>
            </div>
            <Link className="btn btn-primary btn-lg" to={user ? '/account/plan' : '/signup'}>{text('sellButton', 'marketplace.sellBandButton')}</Link>
          </div>
        </section>
      )}
    </>
  );
}
